import axios from "axios";
import moment from "moment/moment";
import React, { useEffect, useState } from "react";
import { Award, Target, Laptop, Zap, Star, Phone, MessageSquare } from "lucide-react";
import CorporateTraining from "./CorporateTraining";
import "./ProgramDetails.custom.css";

const WhyJoinThisProgram = ({ course, loggedIn, courses, user }) => {
  const courseStartDate = course?.courseDate;
  const category = (course?.category || "").toLowerCase();
  const courseName = (course?.CourseName || "").toLowerCase();

  // Get dynamic features based on category
  const getFeatures = () => {
    if (category.includes("ai") || category.includes("artificial") || category.includes("intelligence") || category.includes("generative") || courseName.includes("ai")) {
      return [
        {
          icon: <Award />,
          title: "AI Leadership Credential",
          description: "Earn a prestigious certificate from yslearn Engineering Professional Education",
        },
        {
          icon: <Target />,
          title: "Business-Focused Curriculum",
          description: "Master AI strategies tailored for sales, marketing, analytics, engineering, and more",
        },
        {
          icon: <Laptop />,
          title: "Experience GenAI Use Cases",
          description: "Gain deep insights into GenAI applications through real-world cases studies and peer discussions",
        },
        {
          icon: <Zap />,
          title: "Build Scalable AI Strategies",
          description: "Leverage GenAI with strategic clarity, ethical use, and economic impact",
        },
      ];
    }

    if (category.includes("web") || category.includes("frontend") || category.includes("backend") || category.includes("full stack") || courseName.includes("web") || courseName.includes("developer")) {
      return [
        {
          icon: <Award />,
          title: "Full Stack Certification",
          description: "Earn a professional certificate recognized by top technology recruiters and companies",
        },
        {
          icon: <Target />,
          title: "Hands-on Development",
          description: "Master modern full-stack web architectures, APIs, components, and state management",
        },
        {
          icon: <Laptop />,
          title: "Build Real Applications",
          description: "Develop, review, test, and deploy interactive web applications from scratch",
        },
        {
          icon: <Zap />,
          title: "Accelerate Developer Growth",
          description: "Position yourself for senior engineering and front-end/back-end specialist roles",
        },
      ];
    }

    if (category.includes("cloud") || category.includes("devops") || courseName.includes("cloud") || courseName.includes("aws") || courseName.includes("azure")) {
      return [
        {
          icon: <Award />,
          title: "Cloud Infrastructure Credential",
          description: "Earn a professional credential representing real-world systems management capability",
        },
        {
          icon: <Target />,
          title: "Architecture-Focused Curriculum",
          description: "Master automated deployment strategies, cloud hosting configurations, and security audits",
        },
        {
          icon: <Laptop />,
          title: "Experience Pipelines Hands-on",
          description: "Design CI/CD pipelines, containerize systems, and manage clusters with code",
        },
        {
          icon: <Zap />,
          title: "Scale Digital Operations",
          description: "Leverage cloud architectures to optimize reliability, latency, speed, and hosting costs",
        },
      ];
    }

    // Default general course features
    return [
      {
        icon: <Award />,
        title: "Professional Certification",
        description: "Earn an official certificate demonstrating practical capability from yslearn Education",
      },
      {
        icon: <Target />,
        title: "Industry-Relevant Curriculum",
        description: "Master concepts, skills, and tools aligned directly with active company recruitments",
      },
      {
        icon: <Laptop />,
        title: "Hands-On Experiments",
        description: "Apply concepts through structural coding walkthroughs, laboratories, and projects",
      },
      {
        icon: <Zap />,
        title: "Career Transformation",
        description: "Connect with industry professionals, receive career guidance, and get job ready",
      },
    ];
  };

  const features = getFeatures();

  const [inquirydata, setInquiryData] = useState({
    name: "",
    email: "",
    phone: "",
    inquiry: "Myself",
  });

  const [isChecked, setIsChecked] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setInquiryData({
      ...inquirydata,
      [name]: value,
    });
  };

  const handleCheckboxChange = (e) => {
    setIsChecked(e.target.checked);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isChecked) {
      alert("Please agree to the Privacy Policy before submitting.");
      return;
    }
    try {
      await axios.post(
        `${process.env.REACT_APP_LOGIN_API_BASEURL}/inquiry`,
        inquirydata
      );
      alert("Inquiry submitted successfully!");
    } catch (error) {
      alert(error?.response?.data?.message || error.message);
    }
  };

  const handleScheduleCall = () => {
    const element = document.getElementById("inquiry-form-card");
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "center" });
      const nameInput = document.getElementsByName("name")[0];
      if (nameInput) {
        setTimeout(() => nameInput.focus(), 800);
      }
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">

        {/* ── Left Content Section ── */}
        <div className="lg:col-span-8 space-y-12">
          <div>
            <h2 className="text-4xl font-extrabold text-gray-900 mb-12 tracking-tight">
              Why Join this Program
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {features.map((feature, index) => (
                <div key={index} className="why-join-card relative overflow-visible">
                  <div className="card-icon-wrapper shadow-md">
                    {feature.icon}
                  </div>
                  <div className="mt-4 text-left">
                    <h3 className="text-xl font-bold text-gray-900 mb-3">
                      {feature.title}
                    </h3>
                    <p className="text-gray-500 leading-relaxed text-sm">
                      {feature.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Corporate Training Section */}
          <CorporateTraining />
        </div>

        {/* ── Right Sidebar Form ── */}
        <div className="lg:col-span-4 space-y-8">
          <div id="inquiry-form-card" className="sidebar-form-card sticky top-24">
            <div className="p-8 space-y-6">
              <div className="text-center space-y-4">
                <h3 className="text-lg font-bold text-gray-900 leading-snug">
                  Admissions Close On {moment(courseStartDate).format("Do MMM")}
                </h3>

                {/* Progress Bar */}
                <div className="space-y-2 pt-2">
                  <div className="flex justify-between text-[11px] font-black text-gray-400 uppercase tracking-wider">
                    <span>Enrollment Progress</span>
                    <span className="text-blue-600 font-extrabold">85% Filled</span>
                  </div>
                  <div className="w-full bg-gray-100 rounded-full h-3 overflow-hidden shadow-inner">
                    <div
                      className="bg-gradient-to-r from-blue-600 to-indigo-600 h-full rounded-full transition-all duration-1000"
                      style={{ width: "85%" }}
                    />
                  </div>
                  <p className="text-[11px] text-right text-amber-600 font-bold animate-pulse">
                    Only 15 seats left!
                  </p>
                </div>
              </div>

              <form className="space-y-4" onSubmit={handleSubmit}>
                <input
                  type="text"
                  name="name"
                  placeholder="Name*"
                  required
                  value={inquirydata.name}
                  onChange={handleChange}
                  className="form-input-custom text-sm font-semibold bg-gray-50/50 border border-gray-200/80 rounded-xl px-4 py-3 w-full"
                />
                <input
                  type="email"
                  name="email"
                  placeholder="Email*"
                  required
                  value={inquirydata.email}
                  onChange={handleChange}
                  className="form-input-custom text-sm font-semibold bg-gray-50/50 border border-gray-200/80 rounded-xl px-4 py-3 w-full"
                />
                <div className="flex gap-2">
                  <select className="w-24 bg-gray-50 border border-gray-200 rounded-xl text-xs font-bold px-2 py-3 focus:outline-none">
                    <option>IN +91</option>
                  </select>
                  <input
                    type="tel"
                    name="phone"
                    placeholder="Phone Number*"
                    required
                    value={inquirydata.phone}
                    onChange={handleChange}
                    className="form-input-custom text-sm font-semibold bg-gray-50/50 border border-gray-200/80 rounded-xl px-4 py-3 w-full"
                  />
                </div>

                <div className="flex items-center gap-6 py-2">
                  <span className="text-xs font-bold text-gray-400 uppercase tracking-wide">Inquiry for:</span>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="inquiry"
                      value="Myself"
                      checked={inquirydata.inquiry === "Myself"}
                      onChange={handleChange}
                      className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                    />
                    <span className="text-sm font-bold text-gray-700">Myself</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="inquiry"
                      value="My Company"
                      checked={inquirydata.inquiry === "My Company"}
                      onChange={handleChange}
                      className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                    />
                    <span className="text-sm font-bold text-gray-700">Company</span>
                  </label>
                </div>

                <div className="flex items-start gap-3 py-1">
                  <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={handleCheckboxChange}
                    className="mt-1 w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                  />
                  <span className="text-xs text-gray-400 leading-relaxed font-semibold">
                    By providing details, you agree to our{" "}
                    <a href="#" className="text-blue-600 font-bold hover:underline">
                      Privacy Policy
                    </a>
                  </span>
                </div>

                <button
                  type="submit"
                  className="w-full btn-primary-custom py-4 text-base"
                >
                  Talk to Our Advisor
                </button>
              </form>
            </div>
          </div>

          {/* Schedule a Call Card */}
          <div className="bg-white rounded-3xl border border-gray-100 p-8 shadow-sm text-center space-y-4 hover:shadow-md transition-shadow duration-300">
            <h4 className="text-base font-bold text-gray-900 leading-snug">Know More About This Program</h4>
            <button 
              onClick={handleScheduleCall}
              className="w-full btn-outline-custom py-3.5 flex items-center justify-center gap-2"
            >
              <Phone className="w-4 h-4" />
              Schedule a Call
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WhyJoinThisProgram;
